import type { FC } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import urltemplate from 'url-template';
import type { ContractManagementResponse } from 'src/apis/contract-management/types';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import { useLectTerminationConsequences } from 'src/apis/contract-management/use-lect-termination-consequences';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import {
  useLect,
  type LectRequest,
} from 'src/pages/transport/secondary-trading/trade/use-lect';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

type LoadLectProps = {
  contractId: string;
  contractManagement: ContractManagementResponse;
};

const LoadLect: FC<LoadLectProps> = ({ contractId, contractManagement }) => {
  if (!contractManagement._links.getLect) throw new Forbidden();

  const lectUrl = urltemplate
    .parse(contractManagement._links.getLect.href)
    .expand({ lectId: contractId });

  const lect = useLect({ url: lectUrl });

  if (!lect.response) return <PageSpinner />;

  const terminationConsequencesUrl =
    lect.response.data._links.terminationConsequences?.href;

  if (!terminationConsequencesUrl) throw new Forbidden();

  return (
    <LoadTerminationConsequences
      contractId={contractId}
      lect={lect}
      terminationConsequencesUrl={terminationConsequencesUrl}
    />
  );
};

type LoadTerminationConsequencesProps = {
  contractId: string;
  lect: Successful<LectRequest>;
  terminationConsequencesUrl: string;
};

const LoadTerminationConsequences: FC<LoadTerminationConsequencesProps> = ({
  contractId,
  lect,
  terminationConsequencesUrl,
}) => {
  const lectTerminationConsequences = useLectTerminationConsequences({
    url: terminationConsequencesUrl,
  });

  if (!lectTerminationConsequences.response) return <PageSpinner />;

  return (
    <Page
      contractId={contractId}
      lect={lect}
      lectTerminationConsequences={lectTerminationConsequences}
    />
  );
};

export const TerminateLect: FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const contractManagement = useContractManagement();

  if (!contractManagement.response) return <PageSpinner />;

  return (
    <LoadLect
      contractId={contractId}
      contractManagement={contractManagement.response.data}
    />
  );
};
